import type { useReactAnalytics } from "@asurion-core/eventualize-react";

import { trackFullStoryEvent } from "@expert/monitoring";

import type { HighlightAssistSettled } from "./askAssistAboutHighlight";
import type { TextHighlightSurface } from "./TextHighlightArea";

import { useTimelineStore } from "../../state";

type Dispatcher = ReturnType<typeof useReactAnalytics>["dispatcher"];

interface SentInfo {
    appName: string;
    experimentName?: string;
    messageId: string;
}

const HIGHLIGHT_MESSAGE_SOURCE = "TextHighlight";

/**
 * Reports one highlight ask like any other Assist question and answer (`MessageSent` / `MessageReceived`);
 * `source` and `highlightSurface` tell highlight asks apart, and `outcome` says how each ended.
 */
export function reportHighlightAsk(dispatcher: Dispatcher, surface: TextHighlightSurface) {
    let sent: SentInfo | undefined;
    const common = () => ({
        ...getSessionInfo(),
        expertAssistAppName: sent?.appName,
        expertAssistExperimentName: sent?.experimentName,
        source: HIGHLIGHT_MESSAGE_SOURCE,
        highlightSurface: surface,
    });

    return {
        onSent: (info: SentInfo) => {
            sent = info;
            const { sessionId, callSid, chatId, ...event } = { ...common(), messageId: info.messageId };
            const analyticsInfo = { ...event, messageType: "Expert" };
            void dispatcher.dispatchBusinessEvent("MessageSent", { senderType: "expert", ...analyticsInfo });
            trackFullStoryEvent("ExpertAssistMessageSent", { sessionId, callSid, chatId, ...analyticsInfo });
        },
        onSettled: ({ outcome, durationMs, replyMessageId, replyAnalytics }: HighlightAssistSettled) => {
            const { sessionId, callSid, chatId, ...event } = common();
            const analyticsInfo = {
                ...replyAnalytics,
                ...event,
                messageId: replyMessageId,
                requestMessageId: sent?.messageId,
                messageType: "Search",
                outcome,
                durationMs,
            };
            void dispatcher.dispatchBusinessEvent("MessageReceived", { senderType: "bot", ...analyticsInfo });
            trackFullStoryEvent("ExpertAssistMessageReceived", { sessionId, callSid, chatId, ...analyticsInfo });
        },
    };
}

/** The current timeline session, read when an event fires rather than subscribed to. */
function getSessionInfo() {
    const { currentSessionId: sessionId, getTimelineState } = useTimelineStore.getState();
    const { callSid, chatId, partner } = getTimelineState(sessionId);
    return { sessionId, callSid, chatId, partner };
}
